// AdminPlayground.tsx — /admin/playground (Task 2 of the playground-analytics cycle, spec
// .superpowers/sdd/2026-08-20-playground-analytics/). One fixed-shape dashboard fed by a
// single call to `AdminPlaygroundOverviewDto` (deliberately not paginated — see that DTO's
// own doc comment in `@repo/shared-types`), mirroring AdminFeedback.tsx's query/skeleton/error
// idiom and AdminWorkspaceDetail.tsx's stat-card (`Card` + label + `text-2xl font-semibold`)
// idiom.
//
// Bar strips: no chart library (widget/dashboard-wide convention — see CLAUDE.md "Frontend
// State & UI"), just a flex row of divs sized by `count / max`, matching the brief's spec
// verbatim (min-height 2px so a zero day still shows a baseline tick).
//
// Status chips: `DemoStatus` (pending/crawling/indexing/ready/error/needs_js/expired) is NOT a
// subset of `StatusChipState` (@repo/ui/status-chip.tsx has no `needs_js`/`expired` member —
// those are demo-only terminal states with no source/bot equivalent), so reusing `StatusChip`
// would mean inventing new hues for two of the seven values. Per the brief, plain muted
// rounded spans instead — applied to every status uniformly rather than mixing two visual
// languages in one row.
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import type { AdminPlaygroundOverviewDto } from '@repo/shared-types';
import { Card } from '@repo/ui/components/card';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { ExternalLinkIcon } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import { formatDate as formatDateIntl, formatNumber } from '../locales/format';
import type { Locale } from '../locales';

/** Last 30 UTC days, oldest first, as `YYYY-MM-DD` — the client-side half of the zero-fill
 * (API omits zero-count days, see `AdminPlaygroundOverviewDto`'s doc comment). */
function last30UtcDays(): string[] {
  const days: string[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    d.setUTCDate(d.getUTCDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

function StatCard({ label, value, locale }: { label: string; value: number; locale: Locale }) {
  return (
    <Card className="gap-1.5 p-5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold">{formatNumber(value, locale)}</span>
    </Card>
  );
}

/** "Aug 18" from a UTC `YYYY-MM-DD` key — pinned to UTC so the label names the same
 * calendar day the API bucketed by, whatever the admin's local zone. */
function shortDay(date: string, locale: Locale): string {
  return formatDateIntl(`${date}T00:00:00Z`, locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Flex-row bar strip, zero-filled to `days`. No chart library — plain divs, height
 * proportional to `count / max`, min 2px so a zero day still shows a baseline tick.
 * Hover OR tap a column to get a real tooltip (Johnny, 2026-08-20 — the native `title`
 * attr was desktop-only and slow): each column is a full-height hit target, `active`
 * highlights the bar and floats a label above it; tapping the same column again (or
 * leaving with the mouse) dismisses. */
function BarStrip({
  label,
  unit,
  days,
  counts,
  locale,
}: {
  label: string;
  unit: (args: { count: number }) => string;
  days: string[];
  counts: AdminPlaygroundOverviewDto['demosPerDay'];
  locale: Locale;
}) {
  const [active, setActive] = useState<number | null>(null);
  const byDate = new Map(counts.map((c) => [c.date, c.count]));
  const filled = days.map((date) => ({ date, count: byDate.get(date) ?? 0 }));
  const max = Math.max(1, ...filled.map((d) => d.count));
  const activeDay = active !== null ? filled[active] : undefined;
  return (
    <Card className="gap-3 p-5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="relative flex h-24 items-end gap-[3px]" onMouseLeave={() => setActive(null)}>
        {active !== null && activeDay && (
          <div
            className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 rounded-md border border-border bg-popover px-2 py-1 font-mono text-[11px] whitespace-nowrap text-popover-foreground shadow-md"
            style={{ left: `${((active + 0.5) / filled.length) * 100}%` }}
          >
            {shortDay(activeDay.date, locale)} · {formatNumber(activeDay.count, locale)}{' '}
            {unit({ count: activeDay.count })}
          </div>
        )}
        {filled.map(({ date, count }, i) => {
          const height = Math.max(2, Math.round((count / max) * 76));
          return (
            <button
              type="button"
              key={date}
              aria-label={`${shortDay(date, locale)}: ${count} ${unit({ count })}`}
              className="flex min-w-[2px] flex-1 cursor-default items-end pt-4"
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(active === i ? null : i)}
            >
              <div
                className={`w-full rounded-t-sm ${active === i ? 'bg-primary' : 'bg-primary/70'}`}
                style={{ height: `${height}px` }}
              />
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function StatusRow({
  statusBreakdown30d,
  locale,
  t,
}: {
  statusBreakdown30d: AdminPlaygroundOverviewDto['statusBreakdown30d'];
  locale: Locale;
  t: Catalog;
}) {
  if (statusBreakdown30d.length === 0) {
    return <p className="text-sm text-muted-foreground">{t.admin.playground.noDemos30d}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {statusBreakdown30d.map(({ status, count }) => (
        <span
          key={status}
          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-[3px] text-xs font-medium text-muted-foreground"
        >
          {status}
          <span className="font-semibold text-foreground">{formatNumber(count, locale)}</span>
        </span>
      ))}
    </div>
  );
}

/** Table shape at the real 44px row height (handoff Shell Anatomy §06), matching
 * AdminFeedback.tsx / AdminWorkspaces.tsx's `TableSkeleton`. */
function TableSkeleton() {
  const widths = ['w-[80%]', 'w-[60%]', 'w-[70%]', 'w-[65%]', 'w-[50%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 7 }, (_, i) => (
          <Card key={i} className="gap-1.5 p-5">
            <SkeletonText className="w-32" />
            <Skeleton className="h-8 w-20" />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <SkeletonText className="w-40" />
      <TableSkeleton />
    </div>
  );
}

export function AdminPlayground() {
  const { t, locale } = useLocale();
  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'playground'],
    queryFn: () => apiFetch<AdminPlaygroundOverviewDto>('/api/admin/playground'),
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isPending) return <PageSkeleton />;
  if (error) return <p className="text-destructive">{error.message}</p>;
  if (!data) return null;

  const days = last30UtcDays();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t.admin.playground.demosAllTimeLabel} value={data.totals.demosAllTime} locale={locale} />
        <StatCard label={t.admin.playground.demos30dLabel} value={data.totals.demos30d} locale={locale} />
        <StatCard label={t.admin.playground.messagesAllTimeLabel} value={data.totals.messagesAllTime} locale={locale} />
        <StatCard label={t.admin.playground.signedUpAllTimeLabel} value={data.totals.signedUpAllTime} locale={locale} />
        <StatCard label={t.admin.playground.signedUp30dLabel} value={data.totals.signedUp30d} locale={locale} />
        <StatCard label={t.admin.playground.leadsAllTimeLabel} value={data.totals.leadsAllTime} locale={locale} />
        <StatCard label={t.admin.playground.leads30dLabel} value={data.totals.leads30d} locale={locale} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <BarStrip
          label={t.admin.playground.demosPerDayLabel}
          unit={t.admin.playground.demoUnit}
          days={days}
          counts={data.demosPerDay}
          locale={locale}
        />
        <BarStrip
          label={t.admin.playground.messagesPerDayLabel}
          unit={t.admin.playground.messageUnit}
          days={days}
          counts={data.messagesPerDay}
          locale={locale}
        />
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.playground.statusBreakdownHeading}</h2>
        <StatusRow statusBreakdown30d={data.statusBreakdown30d} locale={locale} t={t} />
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.playground.recentDomainsHeading}</h2>
        {data.recentDomains.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.admin.playground.noDemosYet}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.admin.playground.colHostname}</TableHead>
                  <TableHead>{t.admin.playground.colStatus}</TableHead>
                  <TableHead>{t.admin.playground.colPages}</TableHead>
                  <TableHead>{t.admin.playground.colMessages}</TableHead>
                  <TableHead>{t.admin.playground.colAttempts}</TableHead>
                  <TableHead>{t.admin.playground.colSignedUp}</TableHead>
                  <TableHead>{t.admin.playground.colLeads}</TableHead>
                  <TableHead>{t.admin.playground.colCreated}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recentDomains.map((row) => (
                  <Fragment key={row.hostname}>
                    <TableRow>
                      <TableCell>
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex min-w-0 items-center gap-1 truncate hover:underline"
                        >
                          <span className="truncate">{row.hostname}</span>
                          <ExternalLinkIcon
                            className="size-3.5 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                        </a>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex w-fit items-center rounded-full bg-muted px-2.5 py-[3px] text-xs font-medium whitespace-nowrap text-muted-foreground">
                          {row.status}
                        </span>
                      </TableCell>
                      <TableCell>{formatNumber(row.pageCount, locale)}</TableCell>
                      <TableCell>{formatNumber(row.messageCount, locale)}</TableCell>
                      <TableCell>{formatNumber(row.attempts, locale)}</TableCell>
                      <TableCell>
                        {row.signedUp ? (
                          <span aria-label={t.admin.playground.signedUpAriaLabel}>✓</span>
                        ) : (
                          <span className="text-muted-foreground" aria-label={t.admin.playground.notSignedUpAriaLabel}>
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.leadCount === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <button
                            type="button"
                            aria-expanded={expanded === row.hostname}
                            onClick={() => setExpanded(expanded === row.hostname ? null : row.hostname)}
                            className="font-medium underline underline-offset-2"
                          >
                            {formatNumber(row.leadCount, locale)}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {relativeTime(row.createdAt, t.time)}
                      </TableCell>
                    </TableRow>
                    {expanded === row.hostname && (
                      <TableRow key={`${row.hostname}-leads`}>
                        <TableCell colSpan={8} className="bg-muted/40">
                          <ul className="flex flex-col gap-1.5 py-1">
                            {row.leads.map((lead) => (
                              <li
                                key={`${lead.email ?? lead.name ?? 'anon'}-${lead.createdAt}`}
                                className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs"
                              >
                                <span className="font-medium">{lead.email ?? t.admin.playground.noEmailFallback}</span>
                                {lead.name && (
                                  <span className="text-muted-foreground">{lead.name}</span>
                                )}
                                {lead.intent && (
                                  <span className="text-muted-foreground italic">{lead.intent}</span>
                                )}
                                <span className="ml-auto whitespace-nowrap text-muted-foreground">
                                  {relativeTime(lead.createdAt, t.time)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
